/**
 * Main dashboard home page — Aurora glass theme.
 *
 * Mirrors the legacy AngularJS brew.dashboard.Controller and dashboard.tpl.html.
 * Shows two main entry-point cards: Discover and Build, styled to match
 * the aurora / northern lights login page design.
 */
import { Link, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCompass,
  faScrewdriverWrench,
  faChartLine,
  faArrowRight,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { getModel } from '@/api/models';
import AuroraBackground from '@/components/layout/AuroraBackground';
import { AppFooter, CLASSIFICATION_BANNER_HEIGHT } from '@brew/ui';
import BrewDbIcon from '@/components/brewdb/BrewDbIcon';
import { useAppInfo } from '@/app/useAppInfo';

const dashboardStyles = `
  @keyframes dash-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-5px); }
  }
  .dash-card {
    background: rgb(var(--surface) / 0.75);
    backdrop-filter: blur(24px) saturate(1.2);
    border: 1px solid rgb(var(--brew-blue-light) / 0.1);
    border-radius: 20px;
    transition: all 0.4s ease;
    animation: dash-float 8s ease-in-out infinite;
  }
  .dash-card:nth-child(2) {
    animation-delay: -2s;
  }
  .dash-card:nth-child(3) {
    animation-delay: -4s;
  }
  .dash-card:nth-child(4) {
    animation-delay: -6s;
  }
  .dash-card:hover {
    border-color: rgb(var(--brew-blue-light) / 0.25);
    box-shadow: 0 8px 40px rgb(var(--brew-blue-light) / 0.08);
    transform: translateY(-2px);
  }
  .dash-icon-ring {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgb(var(--brew-blue-light) / 0.04);
    border: 1px solid rgb(var(--brew-blue-light) / 0.12);
    transition: all 0.4s ease;
  }
  .dash-card:hover .dash-icon-ring {
    background: rgb(var(--brew-blue-light) / 0.08);
    border-color: rgb(var(--brew-blue-light) / 0.25);
    box-shadow: 0 0 30px rgb(var(--brew-blue-light) / 0.1);
  }
  .dash-btn {
    display: inline-flex;
    align-items: center;
    padding: 8px 18px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    background: linear-gradient(135deg, rgb(var(--brew-blue) / 0.25), rgb(var(--brew-blue-light) / 0.15));
    border: 1px solid rgb(var(--brew-blue-light) / 0.25);
    color: #7cc8e8;
    transition: all 0.4s ease;
  }
  .dash-card:hover .dash-btn {
    background: linear-gradient(135deg, rgb(var(--brew-blue) / 0.4), rgb(var(--brew-blue-light) / 0.25));
    border-color: rgb(var(--brew-blue-light) / 0.4);
    box-shadow: 0 4px 24px rgb(var(--brew-blue-light) / 0.15);
    color: #c0e4f5;
  }
`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { info } = useAppInfo();

  async function handleNewModel() {
    const id = crypto.randomUUID();
    try { await getModel(id); return; } catch { /* 404 expected */ }
    navigate(`/build/${id}`);
  }

  return (
    <>
      <style>{dashboardStyles}</style>

      <AuroraBackground />

      {/* Content */}
      <div className="min-h-[var(--app-content-height)] flex flex-col items-center justify-center px-4 py-16 pb-24">
        {/* Welcome heading */}
        <div className="text-center mb-12">
          <img
            src="/assets/img/brewlytics-icon--trans.png"
            alt="Brewlytics"
            className="size-14 mb-5 inline-block"
          />
          <h1 className="text-2xl font-semibold tracking-tight text-aurora-text" >
            Welcome to Brewlytics
          </h1>
          <p className="text-sm mt-1.5 text-brew-blue-light/60" >
            What would you like to do today?
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl w-full">
          <Link to="/dashboard/discover" className="dash-card group no-underline flex">
            <div className="p-6 text-center flex flex-col flex-1">
              <div className="dash-icon-ring mx-auto">
                <FontAwesomeIcon icon={faCompass} className="text-3xl text-brew-blue-light" />
              </div>
              <h2 className="text-lg font-semibold mt-4 text-aurora-text" >Discover</h2>
              <p className="mt-2 text-xs leading-relaxed flex-1 text-aurora-text-muted/70" >
                Don't know where to start? Explore brew apps created by the community.
              </p>
              <div className="mt-4">
                <span className="dash-btn">
                  Start Here
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2 text-xs transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </div>
          </Link>

          <button type="button" onClick={handleNewModel} className="dash-card group text-left flex cursor-pointer">
            <div className="p-6 text-center flex flex-col flex-1">
              <div className="dash-icon-ring mx-auto">
                <FontAwesomeIcon icon={faScrewdriverWrench} className="text-3xl text-brew-blue-light" />
              </div>
              <h2 className="text-lg font-semibold mt-4 text-aurora-text" >Build</h2>
              <p className="mt-2 text-xs leading-relaxed flex-1 text-aurora-text-muted/70" >
                Map out your analytic processes as brew workflows. Visualize your workflows as brew apps.
              </p>
              <div className="mt-4">
                <span className="dash-btn">
                  Start Here
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2 text-xs transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </div>
          </button>

          <Link to="/viz" className="dash-card group no-underline flex">
            <div className="p-6 text-center flex flex-col flex-1">
              <div className="dash-icon-ring mx-auto">
                <FontAwesomeIcon icon={faChartLine} className="text-3xl text-brew-blue-light" />
              </div>
              <h2 className="text-lg font-semibold mt-4 text-aurora-text" >Viz</h2>
              <p className="mt-2 text-xs leading-relaxed flex-1 text-aurora-text-muted/70" >
                Coming soon.
              </p>
              <div className="mt-4">
                <span className="dash-btn">
                  Start Here
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2 text-xs transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </div>
          </Link>

          <Link to="/brewdb" className="dash-card group no-underline flex">
            <div className="p-6 text-center flex flex-col flex-1">
              <div className="dash-icon-ring mx-auto">
                <BrewDbIcon cut="full" paint="primary" className="text-3xl" />
              </div>
              <h2 className="text-lg font-semibold mt-4 text-aurora-text" >brew<span className="text-brew-blue-light">DB</span></h2>
              <p className="mt-2 text-xs leading-relaxed flex-1 text-aurora-text-muted/70" >
                Your own private data warehouse. Stream in data, run SQL queries, and share results — no database to manage.
              </p>
              <div className="mt-4">
                <span className="dash-btn">
                  Start Here
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2 text-xs transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </div>
          </Link>
        </div>
      </div>
      <AppFooter
        closeIcon={<FontAwesomeIcon icon={faXmark} />}
        bottomOffset={info?.capco?.enabled ? CLASSIFICATION_BANNER_HEIGHT : 0}
      />
    </>
  );
}
